import { FC, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { RxCross2 } from "react-icons/rx";
// import { IconButton } from "@mui/material";
// import { Close } from "@mui/icons-material";

interface CookiesConsentPopupProps {
  onClose: () => void;
}

const CookiesConsentPopup: FC<CookiesConsentPopupProps> = ({ onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false); // Controls expansion
  const [userInteracted, setUserInteracted] = useState(false); // Tracks if user has clicked
  const [isClosing, setIsClosing] = useState(false); // Tracks closing animation
  const [autoExpanded, setAutoExpanded] = useState(false); // Tracks if auto-expanded
  const [showContent, setShowContent] = useState(false); // Tracks when to show content
  const [isSmallScreen, setIsSmallScreen] = useState(false); // Tracks if screen is below 1101px

  useEffect(() => {
    // Detect screen size changes and set isSmallScreen based on media query
    const mediaQuery = window.matchMedia("(max-width: 1101px)");
    setIsSmallScreen(mediaQuery.matches);

    const handleResize = () => setIsSmallScreen(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleResize);

    return () => mediaQuery.removeEventListener("change", handleResize);
  }, []);

  useEffect(() => {
    const userClosedPopup = localStorage.getItem("userClosedPopup");
    if (userClosedPopup) {
      return; // Exit if user has already closed the popup
    }

    // Auto expand after 7 seconds
    const expandTimeout = setTimeout(() => {
      if (!userInteracted) {
        setIsExpanded(true);
        setAutoExpanded(true); // Auto expand happened
      }
    }, 7000); // Expand after 7 seconds

    // Auto collapse if no interaction after auto expand
    const collapseTimeout = setTimeout(() => {
      if (
        !userInteracted &&
        autoExpanded &&
        !localStorage.getItem("userAcceptCookies")
      ) {
        setIsExpanded(false); // Collapse after 5 seconds if no consent
        localStorage.setItem("userClosedPopup", "true"); // Save the closing action
      }
    }, 12000); // 7 seconds delay + 5 seconds for collapsing

    return () => {
      clearTimeout(expandTimeout);
      clearTimeout(collapseTimeout);
    };
  }, [userInteracted, autoExpanded]);

  useEffect(() => {
    // Show content when the popup is expanded
    if (isExpanded) {
      const showContentTimeout = setTimeout(
        () => {
          setShowContent(true);
        },
        isSmallScreen ? 0 : 600
      ); // No delay for small screens, delay for larger
      return () => clearTimeout(showContentTimeout);
    } else {
      setShowContent(false);
    }
  }, [isExpanded, isSmallScreen]);

  const handleAcceptCookies = () => {
    localStorage.setItem("userAcceptCookies", "true");
    localStorage.setItem("userClosedPopup", "true"); // Save that user interacted and closed the popup
    onClose(); // Call the onClose handler when cookies are accepted
  };

  const handleIconClick = () => {
    setIsExpanded(true); // Expand the popup when the icon is clicked
    setUserInteracted(true); // Stop auto-collapse after interaction
  };

  const handleCloseClick = () => {
    // Immediately hide the content when close is clicked
    setShowContent(false);
    localStorage.setItem("userClosedPopup", "true"); // Save the manual closing action
    // Ravi B 31-Dec-24 added below one line code and commented subsequent code to remove settimeout
    setIsExpanded(false);

    // if (isSmallScreen) {
    //   setIsExpanded(false); // Instantly collapse on small screens
    // } else {
    //   setIsExpanded(false);
    //   setIsClosing(true); // Start the closing animation for larger screens
    //     setIsExpanded(false); // Collapse the popup
    //     setIsClosing(false); // Reset closing state
    //   }, 600); // Match the animation duration (600ms)
  };

  const backgroundColor = isExpanded
    ? "black"
    : isClosing
    ? "black"
    : "transparent"; // Change background color based on state

  return (
    <motion.section
      className="fixed z-[100000] bottom-4 left-4 w-[calc(100%-32px)] max-w-[800px] sm:w-auto min-w-[53px]" // Ensure min width
      initial={{ width: "50px" }}
      animate={{
        width: isExpanded && !isClosing ? (isSmallScreen ? "" : "60%") : "50px",
      }} // Full width for small screens, 60% for larger screens
      transition={{ duration: isSmallScreen ? 0 : 0.6 }} // No animation on small screens
    >
      <div
        style={{
          backgroundColor: backgroundColor,
        }}
        className="flex flex-col sm:flex-row items-center p-2 rounded-lg w-full sm:w-auto"
      >
        <motion.span
          className="inline-flex p-2 rounded-lg bg-[#06526d] text-white cursor-pointer"
          initial={{ scale: 1 }}
          animate={{ scale: isExpanded ? 1 : 1 }}
          onClick={handleIconClick} // Expand the popup on icon click
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.9803 8.5468C17.5123 8.69458 17.0197 8.7931 16.5271 8.7931C14.2118 8.76847 12.3399 6.89655 12.3153 4.58128C12.3153 4.13793 12.3892 3.69458 12.537 3.27586C11.9951 2.68473 11.6995 1.92118 11.6995 1.13301C11.6995 0.812808 11.7488 0.492611 11.8473 0.172414C11.2315 0.0738918 10.6158 0 10 0C4.48276 0 0 4.48276 0 10C0 15.5172 4.48276 20 10 20C15.5172 20 20 15.5172 20 10C20 9.77833 20 9.55665 19.9754 9.33498C19.2611 9.26108 18.5468 8.99015 17.9803 8.5468ZM4.58128 7.31527C6.30542 7.31527 6.30542 10.0246 4.58128 10.0246C2.85714 10.0246 2.61084 7.31527 4.58128 7.31527ZM6.05912 15.7635C4.08867 15.7635 4.08867 12.8079 6.05912 12.8079C8.02956 12.8079 8.02956 15.7635 6.05912 15.7635ZM9.01478 1.33005C10.7389 1.33005 10.7389 4.28571 9.01478 4.28571C7.29064 4.28571 7.04434 1.33005 9.01478 1.33005ZM10.2463 8.84237C11.7241 8.84237 11.7241 10.8128 10.2463 10.8128C8.76848 10.8128 9.01478 8.84237 10.2463 8.84237ZM11.9704 16.9458C10.4926 16.9458 10.4926 14.9754 11.9704 14.9754C13.4483 14.9754 13.202 16.9458 11.9704 16.9458ZM16.6503 13.1034C15.4187 13.1034 15.4187 11.133 16.6503 11.133C17.8818 11.133 17.8818 13.1034 16.6503 13.1034Z"
              fill="currentColor"
            />
          </svg>
        </motion.span>

        {showContent && (
          <motion.div
            className="sm:ml-4 ml-0 p-2 text-white flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto text-center sm:text-left 1437sm:whitespace-nowrap" // Prevent wrapping
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: isSmallScreen ? 0 : 0.6 }} // No animation for small screens
          >
            <p className="text-xs sm:text-sm leading-snug">
              We use only essential cookies to ensure that we give you the best
              experience on our website.
            </p>

            <div className="flex flex-col sm:flex-row sm:justify-end gap-y-2 gap-x-3 w-full sm:w-auto">
              <button
                className="contained " // Prevent shrinking
                onClick={handleAcceptCookies}
              >
                Accept
              </button>
              <button className="contained" onClick={handleAcceptCookies}>
                Decline
              </button>
              <button
                className=" hamburger" // Prevent shrinking
                onClick={handleCloseClick} // Close with or without animation
              >
                <div className="flex items-center hover:text-gray-300 justify-center">
                  <RxCross2 size={20} />
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.section>
  );
};

export default CookiesConsentPopup;
